"use client";

import type { CSSProperties, ReactNode } from "react";
import { Check, Truck } from "@/components/icons";
import { ProductTypeIcon } from "@/lib/icons/product-icons";
import { cn } from "@/lib/utils";
import { DISPLAY_HEADING_CLASSES } from "./display-font";
import type { DemoScenario } from "./use-quote-demo";

interface PieceProps {
  scenario: DemoScenario;
  shown: boolean;
  className?: string;
  style?: CSSProperties;
}

function Appear({ shown, children, className, style }: { shown: boolean; children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <div
      style={style}
      className={cn(
        "transition-[opacity,scale,translate] duration-500 ease-out motion-reduce:transition-none",
        shown ? "opacity-100" : "scale-95 opacity-0",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function RequestBubble({ request, typed, className }: { request: string; typed: number; className?: string }) {
  const isTyping = typed < request.length;
  return (
    <div
      aria-hidden
      className={cn(
        "w-max max-w-full self-end rounded-2xl rounded-br-md bg-surface-2 px-4 py-2.5 text-[15px] font-medium text-foreground",
        className,
      )}
    >
      <span>
        {request.slice(0, typed)}
        <span className={cn("ml-px inline-block h-4 w-0.5 translate-y-0.5 bg-foreground", isTyping ? "animate-pulse" : "opacity-0")} />
      </span>
    </div>
  );
}

export function FreightPill({ scenario, shown, className, style }: PieceProps) {
  return (
    <Appear shown={shown} className={className} style={style}>
      <div className="flex items-center gap-2.5 rounded-full bg-foreground py-1.5 pl-1.5 pr-4 text-sm text-background shadow-lg shadow-foreground/20">
        <span className="flex size-8 items-center justify-center rounded-full bg-brand text-brand-foreground">
          <Truck className="size-4" />
        </span>
        <span className="font-semibold">{scenario.freight}</span>
        <span className="tabular-nums text-background/70">{scenario.freightAmount}</span>
      </div>
    </Appear>
  );
}

function Stamp({ label, shown }: { label: string; shown: boolean }) {
  return (
    <div
      className={cn(
        "absolute -right-3 -top-4 transition-[opacity,scale] duration-300 ease-out motion-reduce:transition-none",
        shown ? "opacity-100" : "scale-150 opacity-0",
      )}
    >
      <span className="inline-flex -rotate-6 items-center gap-1 rounded-md border-2 border-brand-strong bg-surface px-2 py-0.5 text-xs font-bold uppercase tracking-[0.14em] text-brand-strong">
        <Check className="size-3.5" strokeWidth={3} />
        {label}
      </span>
    </div>
  );
}

function InvoiceRow({ label, detail, amount }: { label: string; detail: string; amount: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-dashed border-border py-1.5 text-xs">
      <span className="flex flex-col">
        <span className="font-semibold">{label}</span>
        <span className="text-fg-muted">{detail}</span>
      </span>
      <span className="font-semibold tabular-nums">{amount}</span>
    </div>
  );
}

export function LoadInvoice({ scenario, shown, stamped, className, style }: PieceProps & { stamped: boolean }) {
  return (
    <Appear shown={shown} className={className} style={style}>
      <div className="relative w-[232px] rounded-sm bg-surface p-3.5 shadow-2xl shadow-foreground/20 ring-1 ring-border-subtle">
        <div className="flex items-center gap-3 border-b-2 border-foreground pb-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-brand-tint">
            <ProductTypeIcon slug={scenario.slug} size={30} className="size-[30px]" />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-strong">Load</span>
            <span className="text-[13px] font-semibold leading-tight">{scenario.product}</span>
          </span>
        </div>
        <InvoiceRow label="Pallets" detail={scenario.productDetail} amount={scenario.productAmount} />
        <InvoiceRow label="Freight" detail={scenario.freight} amount={scenario.freightAmount} />
        <div className="flex flex-col gap-1.5 pt-2.5">
          <span className="truncate text-[11px] text-fg-muted">{scenario.totalDetail}</span>
          <span className="flex items-baseline justify-between gap-4">
            <span className="text-xs font-semibold">{scenario.totalLabel}</span>
            <span className={cn(DISPLAY_HEADING_CLASSES, "text-[26px] leading-none tabular-nums")}>{scenario.totalAmount}</span>
          </span>
        </div>
        <Stamp label={scenario.badge} shown={stamped} />
      </div>
    </Appear>
  );
}
